import {
    ArchType,
    FabricationMethod,
    ResinIds,
    TreatmentType,
    TreatmentScanType,
    TreatmentSubType,
    TreatmentFileGroups,
    TreatmentApplianceType,
} from 'app/core/enums';
import {
    BaseImgModel,
    BaseInfoBodyModel,
    BaseInfoModel,
    BaseTooltipModel,
    InfoQuestionModel,
    ItemTooltipModel,
} from 'app/core/models';
import { MomentSubtypeOptions } from 'app/core/constants/treatment/moment-subtype-options';

const treatmentDetailsImagePrefix = `assets/images`;

export const ArchItems: BaseInfoModel[] = [
    {
        id: ArchType.Upper,
        name: 'Upper Arch',
        imagePath: `${treatmentDetailsImagePrefix}/arch-type/Upper-arch.webp`,
        darkModeImagePath: `${treatmentDetailsImagePrefix}/arch-type/Upper-arch-dark.svg`,
    },
    {
        id: ArchType.Lower,
        name: 'Lower Arch',
        imagePath: `${treatmentDetailsImagePrefix}/arch-type/Lower-arch.webp`,
        darkModeImagePath: `${treatmentDetailsImagePrefix}/arch-type/Lower-arch-dark.svg`,
    },
    {
        id: ArchType.Both,
        name: 'Both',
        imagePath: undefined,
    },
];

export const DarkArchItems = ArchItems.map(i => ({
    ...i,
    imagePath: i.imagePath?.replace(/\.webp$/, '-dark.webp'),
}));

export const SubTypes: BaseInfoModel[] = [
    // Occlusal Guard
    {
        name: 'general.flatPlane',
        id: 1,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-flat-plane.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.flatPlane',
            body: {
                sections: [
                    {
                        description: 'messages.flatOcclusalGuardDescription',
                        bullets: [
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet3',
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet4',
                        ],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.occlusalGuardWithAnteriorRamp1',
        id: 2,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-buccal-tilted-anterior-ramp.webp`,
        subtitle: '+$5.00', // TODO: Handle
        euSubtitle: '+€10.00', // TODO: Handle
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.occlusalGuardWithAnteriorRamp1',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalGuardWithAnteriorRampDescription1',
                        bullets: [
                            'messages.occlusalGuardWithAnteriorRampBullet1',
                            'messages.occlusalGuardWithAnteriorRampBullet2',
                        ],
                    },
                    {
                        description: 'messages.occlusalGuardWithAnteriorRampDescription2',
                        bullets: [
                            'messages.occlusalGuardWithAnteriorRampBullet3',
                            'messages.occlusalGuardWithAnteriorRampBullet4',
                        ],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.occlusalGuardWithAnteriorRamp2',
        id: 73,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-lingual-tilted-anterior-ramp.webp`,
        subtitle: '+$5.00', // TODO: Handle
        euSubtitle: '+€10.00', // TODO: Handle
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.occlusalGuardWithAnteriorRamp2',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalGuardWithAnteriorRampDescription1',
                        bullets: [
                            'messages.occlusalGuardWithAnteriorRampBullet1',
                            'messages.occlusalGuardWithAnteriorRampBullet2',
                        ],
                    },
                    {
                        description: 'messages.occlusalGuardWithAnteriorRampDescription2',
                        bullets: [
                            'messages.occlusalGuardWithAnteriorRampBullet3',
                            'messages.occlusalGuardWithAnteriorRampBullet4',
                        ],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.occlusalGuardNoAnterior',
        id: 11,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-without-anterior-coverage.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.occlusalGuardWithoutAnteriorCoverage',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalGuardWithoutAnteriorCoverageDescription',
                        bullets: [
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet1',
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet2',
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet3',
                            'messages.occlusalGuardWithoutAnteriorCoverageBullet4',
                        ],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.michiganSplint',
        id: 9,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-michigan-splint.webp`,
        subtitle: '+$5.00', // TODO: Handle
        euSubtitle: '+€10.00', // TODO: Handle
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.michiganSplint',
            body: {
                sections: [
                    {
                        description: 'messages.michiganSplintDescription',
                        bullets: ['messages.michiganSplintBullet1', 'messages.michiganSplintBullet2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.fullCoverageAnteriorDeprogrammer',
        id: 10,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/ng-full-coverage-anterior-deprogrammer.webp`,
        subtitle: '+$5.00', // TODO: Handle
        euSubtitle: '+€10.00', // TODO: Handle
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.fullCoverageAnteriorDeprogrammer',
            body: {
                sections: [
                    {
                        description: 'messages.fullCoverageAnteriorDeprogrammerDescription',
                        bullets: [
                            'messages.fullCoverageAnteriorDeprogrammerBullet1',
                            'messages.fullCoverageAnteriorDeprogrammerBullet2',
                        ],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    // Hybrid Dentures
    {
        name: 'Schedule Your Appointment',
        id: 43,
        imagePath: undefined,
        subtitle: '+$175.00',
        euSubtitle: '+€175.00',
        hasInfo: true,
        disabled: true,
        tooltip: {
            active: false,
            title: 'Schedule Your Appointment',
            body: {
                sections: [
                    {
                        description: 'messages.thisWillTakeYouOutsideOfSprintRayCloud',
                        bullets: undefined,
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'Next Business Day by Noon',
        id: 44,
        imagePath: undefined,
        subtitle: '+$50.00',
        euSubtitle: '+€50.00',
        hasInfo: false,
        tooltip: undefined,
    },
    {
        name: '3 Business Days',
        id: 45,
        imagePath: undefined,
        hasInfo: false,
        tooltip: undefined,
    },
    // Surgical Guide
    {
        name: 'general.enhancedAsTitle',
        id: 4,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/enchanced.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.enhancedDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.enhancedPoint1', 'messages.enhancedPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.hollywoodAsTitle',
        id: 3,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/hollywood.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.hollywoodDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.hollywoodPoint1', 'messages.hollywoodPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.matureAsTitle',
        id: 8,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/mature.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.matureDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.maturePoint1', 'messages.maturePoint2', 'messages.maturePoint3'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.softenedAsTitle',
        id: 6,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/softened.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.softenedDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.softenedPoint1', 'messages.softenedPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.vigorousAsTitle',
        id: 5,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/vigorous.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.vigorousDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.vigorousPoint1', 'messages.vigorousPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.youthfulAsTitle',
        id: 7,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/youthful.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.youthfulDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.youthfulPoint1', 'messages.youthfulPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: '45 Minutes',
        id: 46,
        imagePath: undefined,
        subtitle: '+$140.00 per arch',
        euSubtitle: '+€150.00 per arch',
        hasInfo: false,
        tooltip: undefined,
    },
    {
        name: '2 Business Days',
        id: 47,
        imagePath: undefined,
        hasInfo: false,
        tooltip: undefined,
    },
    // ==========  Veneer  ==========
    {
        name: 'general.hollywoodAsTitle',
        id: 48,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/hollywood.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.hollywoodDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.hollywoodPoint1', 'messages.hollywoodPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.enhancedAsTitle',
        id: 49,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/enchanced.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.enhancedDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.enhancedPoint1', 'messages.enhancedPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.vigorousAsTitle',
        id: 50,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/vigorous.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.vigorousDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.vigorousPoint1', 'messages.vigorousPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.softenedAsTitle',
        id: 51,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/softened.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.softenedDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.softenedPoint1', 'messages.softenedPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.youthfulAsTitle',
        id: 52,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/youthful.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.youthfulDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.youthfulPoint1', 'messages.youthfulPoint2'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.matureAsTitle',
        id: 53,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/mature.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.matureDesignStyle',
            body: {
                sections: [
                    {
                        description: '',
                        bullets: ['messages.maturePoint1', 'messages.maturePoint2', 'messages.maturePoint3'],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    // ==========  End Veneer  ==========
    ...MomentSubtypeOptions,

    {
        name: 'general.closedBite',
        id: 54,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/closed_bite.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.closedBite',
            body: {
                sections: [
                    {
                        description: 'messages.closedBiteDescription',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
    {
        name: 'general.openBite',
        id: 55,
        imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/open_bite.webp`,
        hasInfo: true,
        tooltip: {
            active: false,
            title: 'general.openBite',
            body: {
                sections: [
                    {
                        description: 'messages.openBiteDescription',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
            cssClass: 'sub-type-tooltip',
        },
    },
];

export const RetentionTooltip: BaseTooltipModel = {
    active: false,
    title: 'general.retention',
    body: {
        sections: [
            {
                description: 'messages.retentionAndTightnessDescription',
            },
        ],
    },
    placement: 'top',
};

const AiCrownContactDistanceInfo: InfoQuestionModel = {
    treatmentTypeId: TreatmentType.AICrown,
    questionId: 8,
    hasInfo: true,
    tooltip: {
        title: 'general.contactsDistance',
        active: false,
        body: {
            sections: [{ description: 'messages.contactsDistanceDescription' }],
        },
        placement: 'top',
    },
    source: [],
};

export const InfoSource: InfoQuestionModel[] = [
    {
        treatmentTypeId: TreatmentType.AIRetainer,
        questionId: 1,
        hasInfo: true,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.thickness',
            body: {
                sections: [{ description: 'messages.retainerThicknessToolTip' }],
            },
            placement: 'top',
        },
        source: [
            {
                id: 1,
            },
            {
                id: 2,
            },
            {
                id: 3,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.AIRetainer,
        questionId: 2,
        hasInfo: true,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.tightness',
            body: {
                sections: [{ description: 'messages.retainerTightnessToolTip' }],
            },
            placement: 'top',
        },
        source: [
            {
                id: 3,
            },
            {
                id: 2,
            },
            {
                id: 1,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.NightGuard,
        questionId: 6,
        hasInfo: true,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Occlusal Plane Smoothness',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalPlaneSmoothnessDescription1',
                    },
                    {
                        description: 'messages.occlusalPlaneSmoothnessDescription2',
                    },
                ],
                imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/occlusal_plane_smoothness.webp`,
            },
            placement: 'top',
        },
        source: [
            {
                id: 1,
                name: 'Smooth & Flat Plane/No indentations',
                imagePath: `${treatmentDetailsImagePrefix}/smoothness/Smooth_flat.webp`,
            },
            {
                id: 2,
                name: 'Anatomical Occlusion/ Indentations',
                imagePath: `${treatmentDetailsImagePrefix}/smoothness/Anatomical_Occlusion.webp`,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.NightGuard,
        questionId: 10,
        hasInfo: false,
        tooltip: undefined,
        source: [
            {
                id: 54,
                name: 'general.closedBite',
                imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/closed_bite.webp`,
                hasInfo: true,
                tooltip: {
                    active: false,
                    title: 'general.closedBite',
                    body: {
                        sections: [
                            {
                                description: 'messages.closedBiteDescription',
                                bullets: [],
                            },
                        ],
                    },
                    placement: 'top',
                    cssClass: 'sub-type-tooltip',
                },
            },
            {
                id: 55,
                name: 'general.openBite',
                imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/open_bite.webp`,
                hasInfo: true,
                tooltip: {
                    active: false,
                    title: 'general.openBite',
                    body: {
                        sections: [
                            {
                                description: 'messages.openBiteDescription',
                                bullets: [],
                            },
                        ],
                    },
                    placement: 'top',
                    cssClass: 'sub-type-tooltip',
                },
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.NightGuard,
        questionId: 7,
        source: [],
        hasInfo: true,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.occlusalThicknessAdjustment',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalThicknessAdjustmentDescription',
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        treatmentTypeId: TreatmentType.AINightGuard,
        questionId: 6,
        hasInfo: false,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Occlusal Surface',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalPlaneSmoothnessDescription1',
                    },
                    {
                        description: 'messages.occlusalPlaneSmoothnessDescription2',
                    },
                ],
                imagePath: `${treatmentDetailsImagePrefix}/treatment-sub-type/occlusal_plane_smoothness.webp`,
            },
            placement: 'top',
        },
        source: [
            {
                id: 1,
                name: 'Smooth & Flat Plane/No indentations',
                imagePath: `${treatmentDetailsImagePrefix}/smoothness/Smooth_flat.webp`,
            },
            {
                id: 2,
                name: 'Anatomical Occlusion/ Indentations',
                imagePath: `${treatmentDetailsImagePrefix}/smoothness/Anatomical_Occlusion.webp`,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.AINightGuard,
        questionId: 7,
        source: [],
        hasInfo: true,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.occlusalThicknessAdjustment',
            body: {
                sections: [
                    {
                        description: 'messages.occlusalThicknessAdjustmentDescription',
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        treatmentTypeId: TreatmentType.HybridDentures,
        questionId: 4,
        hasInfo: false,
        tooltip: undefined,
        source: [],
        // options: [
        //     {
        //         id: 0,
        //         name: '0.00 mm',
        //         showName: true,
        //     },
        //     {
        //         id: 1,
        //         name: '1.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 2,
        //         name: '2.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 3,
        //         name: '3.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 4,
        //         name: '4.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 5,
        //         name: '5.00 mm',
        //         showName: true,
        //     },
        //     {
        //         id: 6,
        //         name: '6.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 7,
        //         name: '7.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 8,
        //         name: '8.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 9,
        //         name: '9.00 mm',
        //         showName: false,
        //     },
        //     {
        //         id: 10,
        //         name: '10.00 mm',
        //         showName: true,
        //     },
        // ],
    },
    {
        treatmentTypeId: TreatmentType.HybridDentures,
        questionId: 5,
        hasInfo: false,
        tooltip: undefined,
        source: [
            {
                id: 1,
                name: 'Direct to MUA',
                imagePath: `${treatmentDetailsImagePrefix}/interface-design/direct-MUA.webp`,
            },
            {
                id: 2,
                name: 'Ti-Base Dess 15.007',
                imagePath: `${treatmentDetailsImagePrefix}/interface-design/ti-Base.webp`,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.HybridDentures,
        questionId: 2,
        hasInfo: false,
        tooltip: undefined,
        source: [
            {
                id: 1,
                name: 'Class one bite',
                imagePath: `${treatmentDetailsImagePrefix}/occlusion-class/Class-One-Bite.webp`,
            },
            {
                id: 2,
                name: 'Class two overbite',
                imagePath: `${treatmentDetailsImagePrefix}/occlusion-class/Class-Two-Overbite.webp`,
            },
            {
                id: 3,
                name: 'Class three underbite',
                imagePath: `${treatmentDetailsImagePrefix}/occlusion-class/Class-Three-Underbite.webp`,
            },
            {
                id: 4,
                name: 'Class three underbite with edge to edge occlusion',
                imagePath: `${treatmentDetailsImagePrefix}/occlusion-class/Class-Three-Underbite-Edge-to-Edge-Occlus.webp`,
            },
            {
                id: 5,
                name: 'Other',
                imagePath: `${treatmentDetailsImagePrefix}/occlusion-class/Other-Bite.webp`,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.AINightGuard,
        questionId: 1,
        hasInfo: true,
        tooltip: RetentionTooltip,
        source: [
            {
                id: 1,
                name: 'general.lessTight',
            },
            {
                id: 2,
                name: 'general.standard',
            },
            {
                id: 3,
                name: 'general.moreTight',
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.CustomBleachingTray,
        questionId: 1,
        hasInfo: true,
        description: 'general.wouldYouLikeToAddBleachingMaterialReservoirsOnTeethSurfaces',
        tooltip: {
            active: false,
            title: 'general.bleachingMaterialReservoir',
            body: {
                sections: [
                    {
                        description: 'messages.reservoirTooltip',
                    },
                ],
            },
            placement: 'top',
        },
        source: [
            {
                id: 1,
                imagePath: `${treatmentDetailsImagePrefix}/bleaching-tray/reservoir-yes.webp`,
            },
            {
                id: 2,
                imagePath: `${treatmentDetailsImagePrefix}/bleaching-tray/reservoir-no.webp`,
            },
        ],
    },
    {
        treatmentTypeId: TreatmentType.AICrown,
        questionId: 4,
        hasInfo: false,
        tooltip: undefined,
        source: [
            {
                id: 5,
                imagePath: `${treatmentDetailsImagePrefix}/ai-crown_occlusal-style_regular.svg`,
                name: 'general.regular',
            },
            {
                id: 6,
                imagePath: `${treatmentDetailsImagePrefix}/ai-crown_occlusal-style_mature.svg`,
                name: 'general.matureAsTitle',
            },
            {
                id: 7,
                imagePath: `${treatmentDetailsImagePrefix}/ai-crown_occlusal-style_youthful.svg`,
                name: 'general.youthfulAsTitle',
            },
        ],
    },
    {
        ...AiCrownContactDistanceInfo,
    },
    {
        ...AiCrownContactDistanceInfo,
        questionId: 13,
    },
    {
        treatmentTypeId: TreatmentType.AICrown,
        questionId: 3,
        hasInfo: true,
        tooltip: {
            title: 'general.occlusalDistance',
            active: false,
            body: {
                sections: [{ description: 'messages.occlusalDistanceDescription' }],
            },
            placement: 'top',
        },
        source: [],
    },
];

export const FileTypeImgs: BaseImgModel[] = [
    {
        id: 1,
        name: 'Upper Scan',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/upper-scan.webp`,
    },
    {
        id: 2,
        name: 'Lower Scan',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/lower-scan.webp`,
    },
    {
        id: 3,
        name: 'Left Side',
        imagePath: `${treatmentDetailsImagePrefix}/bite-scans/left-side.webp`,
    },
    {
        id: 4,
        name: 'Right Side',
        imagePath: `${treatmentDetailsImagePrefix}/bite-scans/right-side.webp`,
    },
    {
        id: 9,
        name: 'CBCT Scanc',
        imagePath: `${treatmentDetailsImagePrefix}/CBCT-scans/CBCT-scans.webp`,
    },
    {
        id: 24,
        name: 'Upper Wax',
        imagePath: `${treatmentDetailsImagePrefix}/wax-rims/upper-wax-rim.webp`,
    },
    {
        id: 35,
        name: 'Lower Wax',
        imagePath: `${treatmentDetailsImagePrefix}/wax-rims/lower-wax-rim.webp`,
    },
    {
        id: 40,
        name: 'Front Smile',
        imagePath: `${treatmentDetailsImagePrefix}/smile-design/front-smile.webp`,
    },
    {
        id: 49,
        name: 'Denture/Wax Setup Scan',
        imagePath: `${treatmentDetailsImagePrefix}/denture-wax-setup/denture-wax-setup.webp`,
    },
    {
        id: 58,
        name: 'Bite Scans',
        imagePath: `${treatmentDetailsImagePrefix}/bite-scans/combo.webp`,
    },
    {
        id: 59,
        name: 'Upper Denture',
        imagePath: `${treatmentDetailsImagePrefix}/denture/upper-denture.webp`,
    },
    {
        id: 60,
        name: 'Lower Denture',
        imagePath: `${treatmentDetailsImagePrefix}/denture/lower-denture.webp`,
    },
    {
        id: 62,
        name: 'Bite Scans',
        imagePath: `${treatmentDetailsImagePrefix}/bite-scans/combo.webp`,
    },
    {
        id: 63,
        name: 'Smile Design Front Smile',
        imagePath: `${treatmentDetailsImagePrefix}/smile-design/front-smile.webp`,
    },
    {
        id: 74,
        name: 'Smile Design Front Smile',
        imagePath: `${treatmentDetailsImagePrefix}/smile-design/front-smile.webp`,
    },
    {
        id: 70,
        name: 'Upper Appliance Scan',
        imagePath: `${treatmentDetailsImagePrefix}/appliance-scan/upper-appliance.webp`,
    },
    {
        id: 71,
        name: 'Lower Appliance Scan',
        imagePath: `${treatmentDetailsImagePrefix}/appliance-scan/lower-appliance.webp`,
    },
    {
        id: 50,
        name: 'Upper Appliance Scan',
        imagePath: `${treatmentDetailsImagePrefix}/tissue-scan/upper-tissue.webp`,
    },
    {
        id: 51,
        name: 'Lower Appliance Scan',
        imagePath: `${treatmentDetailsImagePrefix}/tissue-scan/lower-tissue.webp`,
    },
    {
        id: 37,
        name: 'CEPH',
        imagePath: `${treatmentDetailsImagePrefix}/ceph.webp`,
    },
    {
        id: 38,
        name: 'PANO',
        imagePath: `${treatmentDetailsImagePrefix}/pano.webp`,
    },
    {
        id: 39,
        name: 'Front Face',
        imagePath: `${treatmentDetailsImagePrefix}/front-face.webp`,
    },
    {
        id: 41,
        name: 'Right Side Face',
        imagePath: `${treatmentDetailsImagePrefix}/right-side-face.webp`,
    },
    {
        id: 42,
        name: 'Left Side Face',
        imagePath: `${treatmentDetailsImagePrefix}/left-side-face.webp`,
    },
    {
        id: 46,
        name: 'Upper Jaw',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/upper-scan.webp`,
    },
    {
        id: 47,
        name: 'Lower Jaw',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/lower-scan.webp`,
    },
    {
        id: 43,
        name: 'Front teeth',
        imagePath: `${treatmentDetailsImagePrefix}/front-view.webp`,
    },
    {
        id: 44,
        name: 'Right Side Teeth',
        imagePath: `${treatmentDetailsImagePrefix}/right-side.webp`,
    },
    {
        id: 45,
        name: 'Left Side Teeth',
        imagePath: `${treatmentDetailsImagePrefix}/left-side.webp`,
    },
    {
        id: 15,
        name: 'Front Face',
        imagePath: `${treatmentDetailsImagePrefix}/front-face.webp`,
    },
    {
        id: 72,
        name: 'Upper Antagonist',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/upper-scan.webp`,
    },
    {
        id: 73,
        name: 'Lower Antagonist',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/lower-scan.webp`,
    },
    {
        id: 77,
        name: 'Upper Prepped Teeth',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/prepped-upper-scan.webp`,
    },
    {
        id: 78,
        name: 'Lower Prepped Teeth',
        imagePath: `${treatmentDetailsImagePrefix}/full-arch-scans/prepped-lower-scan.webp`,
    },
    {
        id: 75,
        name: 'Upload your design here',
        imagePath: `${treatmentDetailsImagePrefix}/bite-scans/combo.webp`,
    }
    
];

type ArchTypeFilter = {
    [key in TreatmentType]?: {
        [key in TreatmentSubType]?: {
            [key in ArchType]: number[];
        };
    };
};

type GroupNameArch = {
    [key in ArchType]?: {
        groupId: number;
        name: string;
    }[];
};

export const ExcludeFileMap: ArchTypeFilter = {
    [TreatmentType.RemovableDentures]: {
        [TreatmentSubType.NewDenture]: {
            [ArchType.Upper]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.LowerJaw,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Both]: [
                TreatmentScanType.UpperJaw,
                TreatmentScanType.LowerJaw,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
        },
        [TreatmentSubType.ImmediateDenture]: {
            [ArchType.Upper]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Both]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.DentureLowerStl,
            ],
        },
        [TreatmentSubType.ReferenceDenture]: {
            [ArchType.Upper]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.LowerJaw,
            ],
            [ArchType.Both]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.LowerJaw,
            ],
        },
        [TreatmentSubType.CopyDenture]: {
            [ArchType.Upper]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.DentureLowerStl,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.DentureUpperStl,
                TreatmentScanType.LowerJaw,
            ],
            [ArchType.Both]: [
                TreatmentScanType.WaxUpLowerStl,
                TreatmentScanType.WaxUpUpperStl,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.LowerJaw,
            ],
        },
    },
    [TreatmentType.HybridDentures]: {
        [TreatmentSubType.SameDayByAppointment]: {
            [ArchType.Upper]: [
                TreatmentScanType.LowerAppliance,
                TreatmentScanType.UpperAppliance,
                TreatmentScanType.LowerTissueScan,
                TreatmentScanType.UpperTissueScan,
                TreatmentScanType.BiteScanCombo,
                TreatmentScanType.LeftSide,
                TreatmentScanType.RightSide,
                TreatmentScanType.LowerAntagonist,
                TreatmentScanType.UpperAntagonist,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.LowerAppliance,
                TreatmentScanType.UpperAppliance,
                TreatmentScanType.LowerTissueScan,
                TreatmentScanType.UpperTissueScan,
                TreatmentScanType.BiteScanCombo,
                TreatmentScanType.LeftSide,
                TreatmentScanType.RightSide,
                TreatmentScanType.LowerAntagonist,
                TreatmentScanType.UpperAntagonist,
            ],
            [ArchType.Both]: [
                TreatmentScanType.LowerAppliance,
                TreatmentScanType.UpperAppliance,
                TreatmentScanType.LowerTissueScan,
                TreatmentScanType.UpperTissueScan,
                TreatmentScanType.BiteScanCombo,
                TreatmentScanType.LeftSide,
                TreatmentScanType.RightSide,
                TreatmentScanType.LowerAntagonist,
                TreatmentScanType.UpperAntagonist,
            ],
        },
        [TreatmentSubType.NextBusinessDayByNoon]: {
            [ArchType.Upper]: [
                TreatmentScanType.LowerAppliance,
                TreatmentScanType.LowerTissueScan,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.LowerJaw,
                TreatmentScanType.UpperAntagonist,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.UpperAppliance,
                TreatmentScanType.UpperTissueScan,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.LowerJaw,
                TreatmentScanType.LowerAntagonist,
            ],
            [ArchType.Both]: [
                TreatmentScanType.LowerJaw,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.UpperAntagonist,
                TreatmentScanType.LowerAntagonist,
            ],
        },
        [TreatmentSubType.ThreeBusinessDays]: {
            [ArchType.Upper]: [
                TreatmentScanType.LowerAppliance,
                TreatmentScanType.LowerTissueScan,
                TreatmentScanType.UpperJaw,
            ],
            [ArchType.Lower]: [
                TreatmentScanType.UpperAppliance,
                TreatmentScanType.UpperTissueScan,
                TreatmentScanType.LowerJaw,
            ],
            [ArchType.Both]: [
                TreatmentScanType.LowerJaw,
                TreatmentScanType.UpperJaw,
                TreatmentScanType.UpperAntagonist,
                TreatmentScanType.LowerAntagonist,
            ],
        },
    },
};

export const GroupNamePerArch: GroupNameArch = {
    [ArchType.Upper]: [
        {
            groupId: TreatmentFileGroups.ArcGroup,
            name: 'general.uploadArchScansLower',
        },
        {
            groupId: TreatmentFileGroups.WaxGroup,
            name: 'general.uploadWaxRimScansUpper',
        },
        {
            groupId: TreatmentFileGroups.DentureGroup,
            name: 'general.uploadDentureScansUpper',
        },
        {
            groupId: TreatmentFileGroups.FullArchScans,
            name: 'general.uploadArchScansUpper',
        },
        {
            groupId: TreatmentFileGroups.TissueScans,
            name: 'general.upperTissueScan',
        },
    ],
    [ArchType.Lower]: [
        {
            groupId: TreatmentFileGroups.ArcGroup,
            name: 'general.uploadArchScansUpper',
        },
        {
            groupId: TreatmentFileGroups.WaxGroup,
            name: 'general.uploadWaxRimScansLower',
        },
        {
            groupId: TreatmentFileGroups.DentureGroup,
            name: 'general.uploadDentureScansLower',
        },
        {
            groupId: TreatmentFileGroups.FullArchScans,
            name: 'general.uploadArchScansLower',
        },
        {
            groupId: TreatmentFileGroups.TissueScans,
            name: 'general.lowerTissueScan',
        },
    ],
};

export const FileGroupInfo: ItemTooltipModel[] = [
    {
        id: 12,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'For optimal design results, upload patient bite scans',
            body: {
                sections: [
                    {
                        description: 'messages.forOptimalDesignResultsUploadPatientBiteScans',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 30,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.biteScans',
            body: {
                sections: [
                    {
                        description: 'messages.biteScansContent',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 13,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'CBCT Scans',
            body: {
                sections: [
                    {
                        description: 'messages.pleaseUploadCBCTinZip',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 20,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Denture/Wax Setup Scan',
            body: {
                sections: [
                    {
                        description: 'messages.dentureWaxSetupScanTooltip',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 27,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Upload Appliance Scan',
            body: {
                sections: [
                    {
                        description: 'messages.applianceScanSetUp',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 38,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Bite Scans',
            body: {
                sections: [
                    {
                        description: 'messages.biteScanSetUp',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 24,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Tissue Scans',
            body: {
                sections: [
                    {
                        description: 'messages.tissueScanSetUp',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 23,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Patient Photos tooltip',
            body: {
                sections: [
                    {
                        description: 'messages.patientPhotoSetUp',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 43,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'Upload full arch scans tooltip',
            body: {
                sections: [
                    {
                        description: 'messages.forVeneerUploadPatientArchScans',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 26,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.uploadScansAfterPrepping',
            body: {
                sections: [
                    {
                        description: 'messages.uploadScansAfterPreppingContent',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 46,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.uploadScansAfterImplantPlacement',
            body: {
                sections: [
                    {
                        description: 'messages.uploadScansAfterImplantPlacementContent',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
    {
        id: 47,
        tooltip: {
            active: false,
            cssClass: 'xl',
            title: 'general.biteScans',
            body: {
                sections: [
                    {
                        description: 'messages.biteScansContentForImplantRestoration',
                        bullets: [],
                    },
                ],
            },
            placement: 'top',
        },
    },
];

export const PrinterTypes: BaseInfoModel[] = [
    {
        id: 7,
        name: 'SprintRay Pro 95 S',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/pro-95-s.webp`,
    },
    {
        id: 8,
        name: 'SprintRay Pro 55 S',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/pro-55-s.webp`,
    },
    {
        id: 1,
        name: 'SprintRay Pro 95',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/pro-95.webp`,
    },
    {
        id: 5,
        name: 'SprintRay Pro 55',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/pro-55.webp`,
    },
    {
        id: 6,
        name: 'Other',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/other.webp`,
    },
];

export const LayerThicknessInfo: BaseInfoModel[] = [
    {
        id: 1,
        name: '50 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/50-microns.webp`,
    },
    {
        id: 3,
        name: '100 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/100-microns.webp`,
    },
    {
        id: 4,
        name: '170 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/170-microns.webp`,
    },
];
export const LayerThicknessInfoV2: BaseImgModel[] = [
    {
        id: 1,
        name: '50 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/50-microns-white.webp`,
    },
    {
        id: 3,
        name: '100 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/100-microns-white.webp`,
    },
    {
        id: 4,
        name: '170 Microns',
        imagePath: `${treatmentDetailsImagePrefix}/print-settings/170-microns-white.webp`,
    },
];
export const InfoNotesV2_1: BaseInfoBodyModel[] = [
    {
        id: 8,
        name: 'general.cementedRestorations',
        sections: [
            {
                description: 'messages.cementedRestorationsTreatmentNotesPartOne',
            },
            {
                description: 'messages.cementedRestorationsTreatmentNotesPartTwo',
            },
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartThree',
            },
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartFour',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/L5VSpTg2kbY?si=S8J3z3Dnw8dipYVe', short: false },
    },
];
export const InfoNotes: BaseInfoBodyModel[] = [
    {
        id: TreatmentType.NightGuard,
        name: 'Night Guard',
        sections: [
            {
                description: 'messages.nightGuardTreatmentNotesPartOne',
            },
            {
                description: 'messages.nightGuardTreatmentNotesPartTwo',
            },
            {
                description: 'messages.nightGuardTreatmentNotesPartThree',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/yfgr6Du8LbI?si=EVPmUNdPCfloEJ63', short: false },
    },
    {
        id: TreatmentType.SmileDesign,
        name: 'Smile Design',
        sections: [
            {
                description: 'messages.smileDesignTreatmentNotesPartOne',
            },
            {
                description: 'messages.smileDesignTreatmentNotesPartTwo',
            },
        ],
    },
    {
        id: TreatmentType.BracketRemoval,
        name: 'Bracket Removal',
        sections: [
            {
                description: 'messages.bracketRemovalTreatmentNotesPartOne',
            },
            {
                description: 'messages.bracketRemovalTreatmentNotesPartTwo',
            },
        ],
    },
    {
        id: TreatmentType.SurgicalGuide,
        name: 'Surgical Guide',
        sections: [
            {
                description: 'messages.surgicalGuideTreatmentNotesPartOne',
            },
            {
                description: 'messages.surgicalGuideTreatmentNotesPartTwo',
            },
            {
                description: 'messages.surgicalGuideTreatmentNotesPartThree',
            },
            {
                description: 'messages.surgicalGuideTreatmentNotesPartFour',
            },
        ],
    },
    {
        id: TreatmentType.ClearAligner,
        name: 'Models for Clear Aligner Fabrication',
        sections: [
            {
                description: 'messages.clearAlignersTreatmentNotesPartOne',
            },
            {
                description: 'messages.clearAlignersTreatmentNotesPartTwo',
            },
            {
                description: 'messages.clearAlignersTreatmentNotesPartThree',
            },
        ],
    },
    {
        id: TreatmentType.SingleImplantWithCrown,
        name: 'Single Implant With Crown',
        sections: [
            {
                description: 'messages.singleImplantWithCrownTreatmentNotesPartOne',
            },
            {
                description: 'messages.singleImplantWithCrownTreatmentNotesPartTwo',
            },
        ],
    },
    {
        id: TreatmentType.CrownAndBridge,
        name: 'Crown And Bridge',
        sections: [
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartOne',
            },
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartTwo',
            },
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartThree',
            },
            {
                description: 'messages.crownAndBridgeTreatmentNotesPartFour',
            },
        ],
    },
    {
        id: TreatmentType.CustomBleachingTray,
        name: 'Models for Custom Bleaching Tray Fabrication',
        sections: [
            {
                description: 'messages.customBleachingTreatmentNotesPartOne',
            },
            {
                description: 'messages.customBleachingTreatmentNotesPartTwo',
            },
        ],
    },
    {
        id: TreatmentType.RemovableDentures,
        name: 'Removable Dentures',
        sections: [
            {
                description: 'messages.removableDenturesTreatmentNotesPartOne',
            },
            {
                description: 'messages.removableDenturesTreatmentNotesPartTwo',
            },
            {
                description: 'messages.removableDenturesTreatmentNotesPartThree',
            },
            {
                description: 'messages.removableDenturesTreatmentNotesPartFour',
            },
            {
                description: 'messages.removableDenturesTreatmentNotesPartFive',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/p12RZYjMXY0?si=jY1XByyWmHyBYusW', short: false },
    },
    {
        id: TreatmentType.AINightGuard,
        name: 'AI Nightguard',
        sections: [
            {
                description: 'messages.aiNightguardTreatmentNotesPartOne',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesPartTwo',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesPartThree',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesPartFour',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/WgOwPxVhBJM?si=BFO5mFxJ_l21uck4', short: false },
    },
    {
        id: TreatmentType.AIRetainer,
        name: 'AI Retainer',
        sections: [
            {
                description: 'messages.aiRetainerTreatmentNotesPartOne',
            },
            {
                description: 'messages.aiRetainerTreatmentNotesPartTwo',
            },
            {
                description: 'messages.aiRetainerTreatmentNotesPartThree',
            },
        ],
    },
    {
        id: TreatmentType.HybridDentures,
        name: 'Hybrid Dentures',
        sections: [
            {
                description: 'messages.hybridDentureTreatmentNotesPartOne',
            },
            {
                description: 'messages.hybridDentureTreatmentNotesPartTwo',
            },
            {
                description: 'messages.hybridDentureTreatmentNotesPartThree',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/NXCM5AQ7Xgs?si=rqrgHb3W6QMlyYob', short: false },
    },
    {
        id: TreatmentType.AICrown,
        name: 'AI Crown',
        sections: [
            {
                description: 'messages.aiCrownTreatmentNotesPartOne',
            },
            {
                description: 'messages.aiCrownTreatmentNotesPartTwo',
            },
            {
                description: 'messages.aiCrownTreatmentNotesPartThree',
            },
            {
                description: 'messages.aiCrownTreatmentNotesPartFour',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/GyKCGRvp2lc?si=JDaZokP_n1Hb2Rgf', short: false },
    },
    {
        id: TreatmentType.SmileCorrect,
        name: 'Smile Correct',
        sections: [
            {
                description: 'messages.smileCorrectTreatmentNotesOne',
            },
            {
                description: 'messages.smileCorrectTreatmentNotesTwo',
            },
            {
                description: 'messages.smileCorrectTreatmentNotesThree',
            },
            {
                description: 'messages.smileCorrectTreatmentNotesFour',
            },
        ],
    },
    {
        id: TreatmentType.ImplantRestorations,
        name: 'Implant Restoration',
        sections: [
            {
                description: 'messages.implantRestorationTreatmentNotesPartOne',
            },
            {
                description: 'messages.implantRestorationTreatmentNotesPartTwo',
            },
            {
                description: 'messages.implantRestorationTreatmentNotesPartThree',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/lLP175mHOWk?si=03pLbQymovj77Mbm', short: false },
    },
    {
        id: TreatmentType.Veneers,
        name: 'Veneers',
        sections: [
            {
                description: '',
            },
            {
                description: 'messages.veneersTreatmentNotesOne',
            },
            {
                description: 'messages.veneersTreatmentNotesTwo',
            },
            {
                description: 'messages.veneersTreatmentNotesThree',
            },
            {
                description: 'messages.veneersTreatmentNotesFour',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/cBgZgxjpgvg?si=dyWLYHBSNwGtHD2w', short: true },
    },
    {
        id: TreatmentType.DentalModel,
        name: 'Dental Model',
        sections: [
            {
                description: '',
            },
            {
                description: 'messages.dentalModelTreatmentNotesPartOne',
            },
            {
                description: 'messages.dentalModelTreatmentNotesPartTwo',
            },
            {
                description: 'messages.dentalModelTreatmentNotesPartThree',
            },
        ],
        video: undefined,
    },
    {
        id: TreatmentType.ManualRetainer,
        name: 'Manual Retainer',
        sections: [
            {
                description: 'messages.manualRetainerTreatmentNotesPartOne',
            },
            {
                description: 'messages.manualRetainerTreatmentNotesPartTwo',
            },
            {
                description: 'messages.manualRetainerTreatmentNotesPartThree',
            },
        ],
    },
];

export const InfoNotesForNgCom: BaseInfoBodyModel[] = [
    {
        id: TreatmentType.NightGuard,
        name: 'Night Guard',
        sections: [
            {
                description: 'messages.nightGuardTreatmentNotesPartOne',
            },
            {
                description: 'messages.nightGuardTreatmentNotesForNgComPartOne',
            },
            {
                description: 'messages.nightGuardTreatmentNotesForNgComPartTwo',
            },
            {
                description: 'messages.nightGuardTreatmentNotesPartThree',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/yfgr6Du8LbI?si=EVPmUNdPCfloEJ63', short: false },
    },
    {
        id: TreatmentType.AINightGuard,
        name: 'AI Nightguard',
        sections: [
            {
                description: 'messages.aiNightguardTreatmentNotesPartOne',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesForNgComPartOne',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesForNgComPartTwo',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesForNgComPartThree',
            },
            {
                description: 'messages.aiNightguardTreatmentNotesPartFour',
            },
        ],
        video: { url: 'https://www.youtube.com/embed/WgOwPxVhBJM?si=BFO5mFxJ_l21uck4', short: false },
    },
    {
        id: TreatmentType.DentalModel,
        name: 'Dental Model',
        sections: [
            {
                description: '',
            },
            {
                description: 'messages.dentalModelTreatmentNotesForNgComPartOne',
            },
            {
                description: 'messages.dentalModelTreatmentNotesForNgComPartTwo',
            },
        ],
        video: undefined,
    },
];

export const AnchorPinItems: BaseInfoModel[] = [
    {
        id: 1,
        name: 'Yes',
        imagePath: `${treatmentDetailsImagePrefix}/anchor-pin-slots/Pins-Yes.webp`,
    },
    {
        id: 0,
        name: 'No',
        imagePath: `${treatmentDetailsImagePrefix}/anchor-pin-slots/Pins-No.webp`,
    },
];

export const GuideTypeItems: BaseInfoModel[] = [
    {
        id: 1,
        name: 'Fully Guided',
        imagePath: `${treatmentDetailsImagePrefix}/guide-type/Fully-guided.webp`,
    },
    {
        id: 2,
        name: 'Pilot Guided',
        imagePath: `${treatmentDetailsImagePrefix}/guide-type/Pilot-guided.webp`,
    },
];

export const GuideTypeTooltip: BaseTooltipModel = {
    active: false,
    title: undefined,
    body: {
        sections: [
            {
                description: 'messages.guideTypeDescription',
            },
        ],
    },
    placement: 'top',
};

export const AbutmentTypeTooltip: BaseTooltipModel = {
    active: false,
    title: undefined,
    body: {
        sections: [
            {
                description: 'messages.abutmentTypeTooltip',
            },
        ],
    },
    placement: 'top',
};
export const AbutmentModelInformationTooltip: BaseTooltipModel = {
    active: false,
    title: 'general.abutmentModelInformationSubTitle',
    body: {
        sections: [
            {
                description: 'messages.abutmentModelInformationTooltip',
            },
        ],
    },
    placement: 'top',
};

export const VeneerItems: BaseInfoModel[] = [
    {
        id: 1,
        name: 'general.no-prepVeneers',
        tooltip: {
            title: 'general.no-prepVeneers',
            body: {
                sections: [
                    {
                        description: 'messages.no-prepVeneersDescription',
                    },
                ],
            },
            placement: 'top',
        },
        hasInfo: true,
        imagePath: '',
    },
    // {
    //     id: 2,
    //     name: 'general.preppedVeneers',
    //     tooltip: {
    //         title: 'general.preppedVeneers',
    //         body: {
    //             sections: [
    //                 {
    //                     description: 'messages.preppedVeneersDescription',
    //                 },
    //             ],
    //         },
    //         placement: 'top',
    //     },
    //     hasInfo: true,
    //     imagePath: '',
    // },
];

export const FabricationMethodItems: BaseInfoModel[] = [
    {
        id: FabricationMethod.selfPrint,
        name: 'general.selfPrint',
        tooltip: {
            title: 'general.selfPrint',
            body: {
                sections: [
                    {
                        description: 'messages.selfPrintDescription',
                    },
                ],
            },
            placement: 'top',
        },
        hasInfo: true,
        imagePath: '',
    },
    {
        id: FabricationMethod.printAndShip,
        name: 'general.printAndShip',
        tooltip: {
            title: 'general.printAndShip',
            body: {
                sections: [
                    {
                        description: 'messages.printAndShipDescription',
                    },
                ],
            },
            placement: 'top',
        },
        hasInfo: true,
        imagePath: '',
        subtitle: 'messages.printAndShipTimeNotes',
        subBoldText: 'messages.printAndShipServiceSpecificCountriesOnly',
    },
];

export const QuantityItems: BaseInfoModel[] = [
    {
        id: 0,
        name: 'general.noBackups',
        hasInfo: false,
        imagePath: '',
        total: 0,
    },
    {
        id: 1,
        name: 'general.oneBackup',
        hasInfo: false,
        subtitle: ' ',
        total: 1,
    },
];

export interface AddNightGuardInfoModel extends BaseInfoModel {
    tooth?: string | null;
}

export const AddNightGuardOptions: AddNightGuardInfoModel[] = [
    { id: 0, name: 'general.none', tooth: '0' },
    { id: 1, name: 'general.addNightGuard', tooth: '0', price: 100 },
];

export const ShadeSplit: string = '  Shade:';

export const ShadeItems: BaseInfoModel[] = [
    {
        id: 1,
        name: 'A1',
    },
    {
        id: 2,
        name: 'A2',
    },
    {
        id: 3,
        name: 'B1',
    },
    {
        id: 4,
        name: 'Bleach',
    },
];

export const QuantityTooltip: BaseTooltipModel = {
    active: false,
    title: 'general.quantity',
    body: {
        sections: [
            {
                description: 'messages.quantityTooltipContent',
            },
        ],
    },
    placement: 'top',
};

export const ClosedBiteScansToolTip: BaseTooltipModel = {
    active: false,
    title: 'general.closedBiteScansTitle',
    body: {
        sections: [
            {
                description: 'messages.closedBiteScansContent',
            },
        ],
    },
    placement: 'top',
};

export const OpenBiteScansToolTip: BaseTooltipModel = {
    active: false,
    title: 'general.openBiteScansTitle',
    body: {
        sections: [
            {
                description: 'messages.openBiteScansContentOne',
            },
            {
                description: 'messages.openBiteScansContentTwo',
            },
        ],
    },
    placement: 'top',
};

export const PostPrepScansToolTip: BaseTooltipModel = {
    active: false,
    title: 'general.postPrepScansTitle',
    body: {
        sections: [
            {
                description: 'messages.postPrepScansContent',
            },
        ],
    },
    placement: 'top',
};

export const ScannerOfiTero: BaseInfoBodyModel = {
    id: 1,
    name: 'messages.exportSettingsForiTero',
    sections: [
        {
            description: 'messages.exportSettingsForiTeroOne',
        },
        {
            description: 'messages.exportSettingsForiTeroTwo',
        },
        {
            description: 'messages.exportSettingsForiTeroThree',
        },
    ],
    video: { url: 'https://www.youtube.com/embed/8Wkgx4Jmy0g?si=hC_Nd06KfJJU84T6', short: false },
};

const ngPrintServiceResins = [
    {
        id: ResinIds.SprintRayNightGuardFlex2OceanBlue,
        name: 'general.nightGuardFlex2OceanBlue',
        imagePath: `${treatmentDetailsImagePrefix}/resins/sprintray-NGFlex2-OceanBlue.webp`,
        subtitle: 'messages.nightGuardFlex2OceanBlueDescription',
        isDefault: false,
    },
    {
        id: ResinIds.SprintRayNightGuardFlex2SimplyClear,
        name: 'general.nightGuardFlex2',
        imagePath: `${treatmentDetailsImagePrefix}/resins/sprintray-NGFlex2.webp`,
        subtitle: 'messages.nightGuardFlex2Description',
        isDefault: true,
    },
];

const veneersPrintServiceResins = [
    {
        id: '2001',
        name: 'SprintRay Die and Model Tan',
        filterId: TreatmentApplianceType.RemovableDieModel,
    },
    {
        id: '2040',
        name: 'SprintRay OnX Tough 2',
        filterId: TreatmentApplianceType.Veneer,
    },
];
const dentalModelPrintServiceResins: BaseInfoModel[] = [
    {
        id: '2023',
        name: 'general.sprintRayDentalModelStone',
        subtitle: 'messages.sprintRayDentalModelStoneDescription',
        imagePath: `${treatmentDetailsImagePrefix}/resins/sprintray-dental-model-stone.svg`,
        isDefault: true,
    },
];

export const VeneerTeethSelections: BaseInfoModel[] = [
    {
        id: 4,
        name: '4',
        imagePath: 'assets/images/teeth-selection/4.svg'
    },
    {
        id: 5,
        name: '5',
        imagePath: 'assets/images/teeth-selection/5.svg'
    },
    {
        id: 6,
        name: '6',
        imagePath: 'assets/images/teeth-selection/6.svg'
    },
    {
        id: 7,
        name: '7',
        imagePath: 'assets/images/teeth-selection/7.svg'
    },
    {
        id: 8,
        name: '8',
        imagePath: 'assets/images/teeth-selection/8.svg'
    },
    {
        id: 9,
        name: '9',
        imagePath: 'assets/images/teeth-selection/9.svg'
    },
    {
        id: 10,
        name: '10',
        imagePath: 'assets/images/teeth-selection/10.svg'
    },
    {
        id: 11,
        name: '11',
        imagePath: 'assets/images/teeth-selection/11.svg'
    },
    {
        id: 12,
        name: '12',
        imagePath: 'assets/images/teeth-selection/12.svg'
    },
    {
        id: 13,
        name: '13',
        imagePath: 'assets/images/teeth-selection/13.svg'
    }
];